import { computed, type MaybeRef, unref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useApi } from "@shopexperts/shared";
import type { AdminWalletTransaction } from "@shopexperts/legacy-api";

interface UseAdminOrganizationWalletHoldTransactionsOptions {
  organizationId: MaybeRef<number | null | undefined>;
  enabled?: MaybeRef<boolean>;
}

function isPositiveId(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

export function useAdminOrganizationWalletHoldTransactions(
  options: UseAdminOrganizationWalletHoldTransactionsOptions,
) {
  const api = useApi();
  const organizationId = computed(() => unref(options.organizationId));
  const enabled = computed(
    () => (unref(options.enabled) ?? true) && isPositiveId(organizationId.value),
  );

  const query = useQuery({
    queryKey: computed(() => [
      "admin",
      "wallet",
      "organization",
      organizationId.value,
      "hold-transactions",
    ]),
    queryFn: async () => {
      if (!isPositiveId(organizationId.value)) {
        throw new Error("organizationId is required to load hold transactions");
      }

      return await api.admin.wallet.listOrganizationHoldTransactions({
        params: { organization: organizationId.value },
      });
    },
    enabled,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });

  const transactions = computed<AdminWalletTransaction[]>(() => query.data.value ?? []);

  return {
    transactions,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    query,
  };
}
